import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { completeAccountRemoval } from "../services/accountDeletionService";
import Toast from "./Toast";

const CompleteAccountRemoval: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    type: "success" as "success" | "error" | "warning" | "info",
    message: "",
  });

  const showToast = (type: "success" | "error" | "warning" | "info", message: string) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast((prev) => ({ ...prev, open: false })), 3000);
  };

  const handleComplete = async () => {
    if (!currentUser?.uid) return;
    setLoading(true);
    try {
      await completeAccountRemoval(currentUser.uid);
      window.location.href = "/";
    } catch (error) {
      console.error("Error completing account removal:", error);
      showToast("error", "Something went wrong removing your account. Please try again or contact support.");
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-base-100 px-6">
      <div className="max-w-md w-full bg-base-100 rounded-2xl shadow-md p-8 text-center">
        <h1 className="text-2xl font-semibold text-base-content mb-4">
          Your account is ready to be deleted
        </h1>
        <p className="text-base-content/80 mb-6">
          You confirmed this request a while ago. When you're ready, tap the button below
          to permanently remove your account and all associated data. This cannot be undone.
        </p>

        {!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-md font-medium disabled:opacity-50 transition-all"
          >
            Complete account removal
          </button>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-base-content/70">
              Are you sure? This is your last chance to change your mind.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setConfirming(false)}
                disabled={loading}
                className="bg-base-200 text-base-content px-4 py-2 rounded-md font-medium disabled:opacity-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50 transition-all"
              >
                {loading ? "Removing your account..." : "Yes, delete everything"}
              </button>
            </div>
          </div>
        )}
      </div>

      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default CompleteAccountRemoval;
